import spotlightService from '../SpotlightService';
import { PlaybackError } from './PlaybackError';

/**
 * `GET {DSP_BASE}/video/play/:videoId/:w/:h?device_type=web`
 *
 * The request itself goes through `spotlightService.callAPI` rather than a
 * second hand-rolled fetch: that is where the house conventions already live —
 * the lazily-resolved `getDspBaseUrl()` base, `x-access-token` from
 * TokenService, `x-client-token` / `x-profile-token` injection, the 401/403
 * token-refresh path, network retries and the circuit breaker. Duplicating any
 * of it here would mean two places to keep in sync.
 *
 * `cacheKey` is deliberately null: the manifest URL is signed and per-viewer,
 * so it must never be served from cache or deduped across videos.
 */

/**
 * HTTP status -> PlaybackError code, per the PINS error table.
 *
 * 401 joins 403: the table's 403 row is "not entitled / login required", and an
 * unauthenticated request is the same story to the viewer.
 * A geo-block is NOT here — it arrives as a 200 whose body carries
 * `geoCheck: false`, and `manifestFromPlayResponse` is what detects it.
 */
const STATUS_TO_CODE = {
  401: 'locked',
  403: 'locked',
  404: 'not_found',
  500: 'not_found', // {error:"Error getting company information for video."} = unknown video id
  503: 'manifest_unavailable',
};

/** Round a rendered CSS pixel size to a positive integer, else use `fallback`. */
function toPixels(value, fallback) {
  const rounded = Math.round(Number(value));
  return Number.isFinite(rounded) && rounded > 0 ? rounded : fallback;
}

/**
 * @param {string} videoId - the DSP oid
 * @param {{width?: number, height?: number}} [size] - the video element's own
 *   rendered size. Anything non-positive (hidden, not yet laid out, jsdom)
 *   falls back to 1920x1080.
 * @returns {Promise<object>} the parsed response body
 * @throws {PlaybackError}
 */
export async function fetchPlayResponse(videoId, size = {}) {
  const width = toPixels(size.width, 1920);
  const height = toPixels(size.height, 1080);
  const path = `video/play/${encodeURIComponent(videoId)}/${width}/${height}?device_type=web`;

  try {
    return await spotlightService.callAPI(path, null, { requiresClientAuth: true });
  } catch (error) {
    // Anything without a mapped status — a network failure, an open circuit
    // breaker, an unconfigured base URL — is "our side cannot answer", which is
    // exactly what manifest_unavailable says. It is never swallowed.
    throw new PlaybackError(STATUS_TO_CODE[error?.status] || 'manifest_unavailable', {
      cause: error,
    });
  }
}

export default fetchPlayResponse;
